import { useTranslation } from "react-i18next";
import { CreateTenderFormGroups, StepperState } from "@/constants/enums";
import { Dispatch, SetStateAction } from "react";
import { SparklesIcon } from "@heroicons/react/24/outline";
import { FormGroupItem } from "./types";
import { TenderType } from "@/graphql/__generated__/graphql";
import { scrollToElementWithOffset } from "./helpers";
import { cn } from "@/lib/utils";

/**
 * Interface for a navlink object to display in the side nav.
 * @property label - The label to display in the side nav
 * @property formGroup - The form group to navigate to
 * @property description - The description to display in the side nav
 * @property state - The state of the form group
 */
interface CreateTenderNavlink {
  label: string;
  formGroup: CreateTenderFormGroups;
  description: string;
  state: StepperState;
}

/**
 * Function to get the state of a form group based on the form state.
 * Used to render the colours of the stepper items.
 * @param formGroup - Main form group to check
 * @param additionalFormGroups - Additional form groups to check
 * @param form - The form object
 * @param formGroupSchemas - The form group schemas to check against
 * @returns The state of the form group
 */
const getFormGroupState = (formGroup: CreateTenderFormGroups, additionalFormGroups: CreateTenderFormGroups[], form: any, formGroupSchemas: any) => {
  const errors = form.formState.errors;
  const formValues = form.getValues();

  // Combine the main formGroup with any additional form groups
  const formGroupsToCheck = [formGroup, ...additionalFormGroups];

  let hasError = false;
  let allComplete = true;
  let allValid = true;

  for (const group of formGroupsToCheck) {
    // Check if there are any errors in the form group
    if (errors[group]) {
      hasError = true;
    }

    // Retrieve the values for the form group
    const formGroupValues = formValues[group];

    // Check if the form group is complete
    const isGroupComplete = formGroupValues && Object.keys(formGroupValues).length > 0;
    allComplete = allComplete && isGroupComplete;

    // Get the schema for the form group
    const formGroupSchema = formGroupSchemas[group];

    if (formGroupSchema && isGroupComplete) {
      // Prepare the value to parse by including the form group key
      const parseValue = { [group]: formGroupValues };

      // Validate the form group values
      const parseResult = formGroupSchema.safeParse(parseValue);

      // Update validity status
      if (!parseResult.success) {
        allValid = false;
      }
    } else {
      // If no schema found or group is incomplete, mark as invalid
      allValid = false;
    }
  }

  // Determine the state based on errors and completeness
  if (hasError) {
    return StepperState.ERROR;
  } else if (allComplete && allValid) {
    return StepperState.DONE;
  } else {
    return StepperState.PENDING;
  }
};

interface renderStepperItemProps {
  navlinkObj: any;
  index: number;
  isLastItem: boolean;
  handleNavigate: any;
  activeFormGroup: CreateTenderFormGroups;
};

const renderStepperItem = (props: renderStepperItemProps) => {
  const { navlinkObj, index, isLastItem, handleNavigate, activeFormGroup } = props;

  const stateClassMap = {
    [StepperState.ERROR]: {
      bg: "bg-red-400",
      border: "border-red-400",
    },
    [StepperState.DONE]: {
      bg: "bg-green-400",
      border: "border-green-400",
    },
    [StepperState.PENDING]: {
      bg: "bg-gray-300",
      border: "border-gray-300",
    },
  };

  const stateClasses = stateClassMap[navlinkObj.state];

  const stateBgColour: string = stateClasses.bg;
  const stateBorderColour: string = stateClasses.border;

  const isActive = navlinkObj.formGroup === activeFormGroup;

  return (
    // CONTAINER
    <div className="flex flex-row flex-start" key={index}>
      {/* STEPPER */}
      <div className="relative top-0 pl-2 pr-1 pt-[6px]">
        <div className={cn("h-4 w-4 rounded-full", stateBgColour)}></div>
        {
          !isLastItem && (
            <div className={cn("h-full border-l-2 absolute top-5 left-[15px]", stateBorderColour)}></div>
          )
        }
      </div>

      {/* ITEM */}
      <a
        href={`#${navlinkObj.formGroup}`}
        className={cn(
          "min-w-[140px] whitespace-nowrap grow py-1 px-2 rounded-md text-sm text-left hover:bg-slate-200",
          isActive && "bg-slate-900 text-slate-50 hover:bg-slate-900/90 dark:bg-slate-50 dark:text-slate-900 dark:hover:bg-slate-50/90"
        )}
        onClick={e => {
          e.preventDefault();
          scrollToElementWithOffset(`${navlinkObj.formGroup}`, 70, "form-scroll-container");
          handleNavigate(navlinkObj.formGroup);
        }}
      >
        <div>{navlinkObj.label}</div>
      </a>
    </div>
  );
};

interface Props {
  formGroups: FormGroupItem[];
  activeFormGroup: CreateTenderFormGroups;
  form?: any;
  aiPrefilled?: boolean;
  inSheet?: boolean;
  setIsOpen?: Dispatch<SetStateAction<boolean>>;
  formGroupSchemas: any;
  tenderType?: TenderType;
};

function CreateTenderSideNavMenu(props: Props) {
  const {
    formGroups,
    activeFormGroup,
    form,
    aiPrefilled = false,
    inSheet,
    setIsOpen,
    formGroupSchemas,
    tenderType,
  } = props;

  // Hooks
  const { t } = useTranslation();

  // Create an array of navlinks to display in the side nav from the form groups
  const tenderNavlinks: CreateTenderNavlink[] = formGroups.map((formGroup) => ({
    label: formGroup.label,
    formGroup: formGroup.group,
    description: formGroup.description,
    state: getFormGroupState(formGroup.group, formGroup.additionalFormGroup ?? [], form, formGroupSchemas)
  }));

  /**
   * Function to handle navigation to a specific form group.
   * @param formGroup - The form group to navigate to
   */
  const handleNavigate = (formGroup: CreateTenderFormGroups): void => {
    const card = document.getElementById(`${formGroup}`);
    if (card) {
      card.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    if (inSheet && setIsOpen) {
      setIsOpen(false);
    }
  };

  return (
    <div className={cn("min-w-3", inSheet ? "w-full" : "w-[220px]")}>
      <nav className="create-tender-side-nav" data-cy="side-nav-container">
        {
          aiPrefilled && (
            <div className="flex items-center text-blue-500">
              <SparklesIcon className="h-4 w-4 mr-2" />
              {t("newtender.importedwithai")}
            </div>
          )
        }
        <div className="p-2 bg-slate-100 rounded-md">
          <div className="flex items-center justify-between">
            <h1 className="text-sm text-slate-400 py-1 px-2">{tenderType === TenderType.QuickOffer ? t("newtender.titlequick") : t("newtender.title")}</h1>
          </div>
          <div className="flex flex-col gap-2">
            {
              tenderNavlinks.map((navlinkObj, index) => {
                const isLastItem = index === tenderNavlinks.length - 1;
                return renderStepperItem({
                  navlinkObj,
                  index,
                  isLastItem,
                  handleNavigate: (formGroup: CreateTenderFormGroups) => {
                    // Prevent default anchor behavior
                    // (the original inline code used e.preventDefault())
                    handleNavigate(formGroup);
                  },
                  activeFormGroup: activeFormGroup,
                });
              })
            }
          </div>
        </div>
      </nav>
    </div>
  );
}

export default CreateTenderSideNavMenu;
